import React, {useEffect} from 'react';
import {CalendarIcon, ClockIcon} from '@heroicons/react/24/outline';

export interface DateTimePickerProps {
  /**
   * The current datetime value as ISO string (e.g., "2026-01-16T15:42:00")
   * or null if no value selected
   */
  value: string | null;

  /**
   * Callback when the datetime changes
   * Returns ISO string without timezone (e.g., "2026-01-16T15:42:00")
   */
  onChange: (value: string | null) => void;

  /**
   * ID for the date input (accessibility)
   */
  dateInputId?: string;

  /**
   * ID for the time input (accessibility)
   */
  timeInputId?: string;

  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Callback when the input loses focus
   */
  onBlur?: () => void;
}

/**
 * DateTimePicker component for selecting both date and time.
 * Uses native HTML5 inputs for best mobile support.
 * Stores values WITHOUT timezone - preserves user's selected time exactly.
 *
 * Layout: Side by side on desktop, stacked on mobile for better UX.
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  dateInputId = 'date-picker',
  timeInputId = 'time-picker',
  disabled = false,
  error,
  onBlur,
}) => {
  // Default to current date/time if value is null or empty
  // Only runs once when the component mounts with a null value
  useEffect(() => {
    if (!value && !disabled) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      onChange(`${year}-${month}-${day}T${hours}:${minutes}:00`);
    }
  }, []); // Only run on mount

  // Parse the ISO string to extract date and time parts
  const dateValue = value ? value.split('T')[0] : '';
  const timeValue = value ? value.split('T')[1]?.substring(0, 5) : '12:00';

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    if (!newDate) {
      onChange(null);
      return;
    }
    // Combine with existing time or default to noon
    const time = value ? value.split('T')[1]?.substring(0, 5) : '12:00';
    onChange(`${newDate}T${time}:00`);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    if (!value) {
      // If no date selected yet, use today's date
      const today = new Date().toISOString().split('T')[0];
      onChange(`${today}T${newTime}:00`);
      return;
    }
    // Keep existing date, update time
    const date = value.split('T')[0];
    onChange(`${date}T${newTime}:00`);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Date Input */}
        <div className="flex-1">
          <label htmlFor={dateInputId} className="block text-xs  text-muted mb-1.5">
            Date
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon className="h-5 w-5 text-disabled" />
            </div>
            <input
              id={dateInputId}
              type="date"
              value={dateValue}
              onChange={handleDateChange}
              onBlur={onBlur}
              disabled={disabled}
              className={`block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border
                text-main
                focus:outline-none focus:ring-2 focus:border-transparent
                transition-all shadow-sm hover:border-interactive-hover
                disabled:opacity-50 disabled:cursor-not-allowed
                [color-scheme:light]
                ${error ? 'border-error focus:ring-error' : 'border-interactive focus:ring-brand'}`}
            />
          </div>
        </div>

        {/* Time Input */}
        <div className="flex-1 sm:max-w-[180px]">
          <label htmlFor={timeInputId} className="block text-xs  text-muted mb-1.5">
            Time
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <ClockIcon className="h-5 w-5 text-disabled" />
            </div>
            <input
              id={timeInputId}
              type="time"
              value={timeValue}
              onChange={handleTimeChange}
              onBlur={onBlur}
              disabled={disabled}
              className={`block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border
                text-main
                focus:outline-none focus:ring-2 focus:border-transparent
                transition-all shadow-sm hover:border-interactive-hover
                disabled:opacity-50 disabled:cursor-not-allowed
                [color-scheme:light]
                ${error ? 'border-error focus:ring-error' : 'border-interactive focus:ring-brand'}`}
            />
          </div>
        </div>
      </div>
      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
};
